import * as React from "react";
import { toast as sonnerToast, Toaster } from "sonner";
import { Button } from "./button";

// Re-export under a local name so `tsc --declaration` doesn't try to surface
// sonner's internal `PromiseIExtendedResult` type (which isn't exported).
export const toast = sonnerToast as unknown as {
  success: (msg: string, opts?: { description?: string }) => void;
  error: (msg: string, opts?: { description?: string }) => void;
  info: (msg: string, opts?: { description?: string }) => void;
  dismiss: (id?: string | number) => void;
  custom: (
    render: (id: string | number) => React.ReactElement,
    opts?: { duration?: number },
  ) => void;
};

/** App-wide toast portal. Mount once near the router root. */
export const ToastProvider: React.FC = () => (
  <Toaster
    position="bottom-center"
    duration={4000}
    closeButton={false}
    toastOptions={{
      classNames: {
        toast:
          "border border-line-strong bg-paper text-ink shadow-lg rounded-md " +
          "px-4 py-3 flex items-start gap-3",
        title: "font-semibold",
        description: "text-sm text-ink-muted",
        actionButton: "ml-2",
      },
    }}
  />
);

interface UndoToastArgs {
  title: string;
  description?: string;
  expiresAt: Date | null; // server-tracked undo window
  onUndo: () => void | Promise<void>;
  // Localized label for the action button; receives the live countdown seconds.
  undoLabel?: (seconds: number) => string;
}

/**
 * Show a toast with an Undo button + live countdown to `expiresAt`. The toast
 * auto-dismisses when the window closes — but the action is only enabled while
 * `Date.now() < expiresAt`.
 */
export function useUndoToast() {
  return React.useCallback(
    ({ title, description, expiresAt, onUndo, undoLabel }: UndoToastArgs) => {
      if (!expiresAt) return;
      const ms = expiresAt.getTime() - Date.now();
      if (ms <= 0) return;
      sonnerToast.custom(
        (id) => (
          <UndoCountdown
            id={String(id)}
            title={title}
            {...(description ? { description } : {})}
            {...(undoLabel ? { undoLabel } : {})}
            expiresAt={expiresAt}
            onUndo={async () => {
              await onUndo();
              sonnerToast.dismiss(id);
            }}
          />
        ),
        { duration: ms },
      );
    },
    [],
  );
}

interface UndoCountdownProps {
  id: string;
  title: string;
  description?: string;
  expiresAt: Date;
  onUndo: () => void | Promise<void>;
  undoLabel?: (seconds: number) => string;
}

function UndoCountdown({
  id,
  title,
  description,
  expiresAt,
  onUndo,
  undoLabel = (s) => `Undo (${s}s)`,
}: UndoCountdownProps) {
  const [remaining, setRemaining] = React.useState(() =>
    Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 1000)),
  );
  React.useEffect(() => {
    const t = setInterval(() => {
      const r = Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 1000));
      setRemaining(r);
      if (r === 0) clearInterval(t);
    }, 500);
    return () => clearInterval(t);
  }, [expiresAt]);

  return (
    <div
      id={id}
      className="flex min-w-[320px] items-start gap-3 rounded-md border border-line-strong bg-paper px-4 py-3 text-ink shadow-lg"
    >
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        {description ? <div className="text-ink-muted text-sm">{description}</div> : null}
      </div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={onUndo}
        disabled={remaining === 0}
      >
        {undoLabel(remaining)}
      </Button>
    </div>
  );
}
